import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableBody,
  // TableFooter,
  TableHead,
  // TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  NoDataContainer,
  // pageSize,
  // paginationLabel,
  StyledTableCell,
  TableBorderRadiusTopLeftRight,
  // TablePaginationStyle,
} from "../../Common/styles/table";
import { useParams } from "react-router-dom";
import http from "../../../utils/http";
// import { AxiosResponse } from "axios";
// import { DateTime } from "luxon";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import {
  CloseRounded,
  CloudDownload,
  History,
  MoreVert,
} from "@mui/icons-material";
import { GridSearchIcon } from "@mui/x-data-grid";
import { debounce } from "lodash";
import VersionHistoryModal from "./VersionHistoryModal";
import AddVersionModal from "./AddVersionModal";
import EditDocumentModal from "./EditDocumentModal";
import StatusBadge from "../../Common/UI/StatusTag";
import { DateTime } from "luxon";
import { v4 as uuid } from "uuid";
import { PARTICIPANT_LOCKED_MESSAGE } from "../../../utils/participantLock";

export const documentTypeOptions = [
  { label: "Protocol Documents", value: "Protocol Documents" },
  { label: "IRB document", value: "IRB document" },
  { label: "Agreements", value: "Agreements" },
];

export const documentStatusColor: any = {
  active: {
    variant: "active",
    label: "Active",
  },
  inactive: {
    variant: "cancelled",
    label: "Inactive",
  },
};

type Props = {
  toggleLoader: boolean;
  setToggleLoader: React.Dispatch<React.SetStateAction<boolean>>;
  isLocked?: boolean;
};

const GlobalDocuments = ({
  toggleLoader,
  setToggleLoader,
  isLocked,
}: Props) => {
  const { participantId } = useParams();

  const [loading, setLoading] = useState(true);
  const [downloadLoading, setDownloadLoading] = useState("");
  const [documentsData, setDocumentsData] = useState<any>([]);
  // const [documentsCount, setDocumentsCount] = useState(0);
  // const [page, setPage] = useState(0);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showAddVersionModal, setShowAddVersionModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [menuLoader, setMenuLoader] = useState("");
  const [searchText, setSearchText] = useState("");
  const [searchId, setSearchId] = useState(uuid());

  const modifySearchTerm = useMemo(
    () =>
      debounce((val: string) => {
        setSearchText(val);
      }, 500),
    [setSearchText]
  );

  const handleClearSearch = () => {
    setSearchText("");
    setSearchId(uuid());
  };

  useEffect(() => {
    const fetchDocumentsData = async () => {
      try {
        setLoading(true);

        let url = `/participant-documents/registry?participantId=${participantId}`;
        if (searchText) {
          url += `&query=${searchText}`;
        }
        const res = await http.get(url);
        const data = res.data?.data;

        const formattedData = data?.map((item: any) => ({
          id: item?.id,
          documentName: item?.name || "",
          type: item?.type || "",
          category: item?.category || "",
          version: item?.versions?.[0]?.version || "",
          versionId: item?.versions?.[0]?.id || "",
          uploadDate: item?.createdAt
            ? DateTime.fromISO(item?.createdAt).toFormat("dd/MM/yyyy")
            : "",
          fileUrl: item?.filePath || item?.url,
          status: item?.status || "",
          hasMoreVersions: item?.versions?.[0]?.version > 1,
        }));

        setDocumentsData(formattedData || []);
        // setDocumentsCount(data?.count || 0);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        errorToastMessage(err as Error);
      }
    };
    fetchDocumentsData();
  }, [participantId, toggleLoader, searchText]);

  // const handleChangePage = (_1: any, newPage: number) => {
  //   setPage(newPage);
  // };

  const handleVersionHistory = async (document: any) => {
    setSelectedDocument({
      ...document,
    });
    setShowVersionModal(true);
  };

  const closeVersionModal = () => {
    setShowVersionModal(false);
    setSelectedDocument(null);
  };

  const closeAddVersionModal = () => {
    setShowAddVersionModal(false);
    setSelectedDocument(null);
  };

  const handleEditDocument = (document: any) => {
    setSelectedDocument({
      ...document,
    });
    setShowEditModal(true);
    handleMenuClose();
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedDocument(null);
  };

  const refreshDocumentsData = () => {
    setToggleLoader((prev) => !prev);
  };

  const handleDownload = async (id: string, versionId: string) => {
    try {
      setDownloadLoading(id);
      let res = await http.get(
        `/participant-documents/registry/${id}/version/${versionId}?participantId=${participantId}`
      );

      const downloadLink = res?.data?.data?.previewUrl;
      window.open(downloadLink, "_blank");
      toastMessage("success", res.data.message);
      setDownloadLoading("");
    } catch (err) {
      setDownloadLoading("");
      errorToastMessage(err as Error);
    }
  };

  const handleAddNewVersion = (row: any) => {
    setSelectedDocument({
      ...row,
    });
    setShowAddVersionModal(true);
    handleMenuClose();
  };

  const handleStatusChange = async (row: any) => {
    try {
      setMenuLoader("statusChange");
      const body = {
        status: row.status === "active" ? "inactive" : "active",
        participantId,
      };
      const res = await http.patch(
        `/participant-documents/registry/${row.id}/status`,
        body
      );
      toastMessage("success", res.data.message);
      setMenuLoader("");
      refreshDocumentsData();
      handleMenuClose();
    } catch (err) {
      errorToastMessage(err as Error);
      setMenuLoader("");
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setSelectedRow(row);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  return (
    <Box sx={{ p: 2, backgroundColor: "#fff", borderRadius: "10px" }}>
      <TextField
        placeholder="Search documents"
        sx={{ width: "30%", mb: 3 }}
        key={searchId}
        defaultValue={searchText}
        onChange={(e) => modifySearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <GridSearchIcon />
            </InputAdornment>
          ),
          endAdornment: searchText && (
            <InputAdornment position="end">
              <IconButton onClick={handleClearSearch}>
                <CloseRounded />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Table sx={TableBorderRadiusTopLeftRight}>
        <TableHead>
          <TableRow>
            <StyledTableCell>Document Name</StyledTableCell>
            <StyledTableCell>Type</StyledTableCell>
            <StyledTableCell>Category</StyledTableCell>
            <StyledTableCell>Version</StyledTableCell>
            <StyledTableCell>Upload Date</StyledTableCell>
            <StyledTableCell>Status</StyledTableCell>
            <StyledTableCell>Download</StyledTableCell>
            <StyledTableCell align="right" />
          </TableRow>
        </TableHead>
        {!loading && documentsData?.length > 0 && (
          <>
            <TableBody>
              {documentsData?.map((row: any) => (
                <TableRow key={row?.id}>
                  <StyledTableCell>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "regular",
                        color: "text.primary",
                      }}
                    >
                      {row?.documentName || "-"}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "regular",
                        color: "text.primary",
                        textTransform: "capitalize",
                      }}
                    >
                      {row?.type || "-"}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "regular",
                        color: "text.primary",
                        textTransform: "capitalize",
                      }}
                    >
                      {row?.category || "-"}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: "regular",
                          color: "text.primary",
                        }}
                      >
                        {row?.version || "-"}
                      </Typography>
                      {row?.hasMoreVersions && (
                        <IconButton
                          onClick={() => handleVersionHistory(row)}
                          sx={{
                            color: "primary.main",
                            padding: "4px",
                          }}
                        >
                          <History fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: "regular",
                        color: "text.primary",
                      }}
                    >
                      {row?.uploadDate || "-"}
                    </Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <StatusBadge
                      label={
                        documentStatusColor[row?.status]?.label ?? row?.status
                      }
                      variant={documentStatusColor[row?.status]?.variant}
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    {downloadLoading === row?.id ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          height: "54px",
                        }}
                      >
                        <CircularProgress size={20} />
                      </Box>
                    ) : (
                      <IconButton
                        onClick={() => handleDownload(row.id, row.versionId)}
                        disabled={!!downloadLoading}
                      >
                        <CloudDownload />
                      </IconButton>
                    )}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <IconButton onClick={(e) => handleMenuClick(e, row)}>
                      <MoreVert />
                    </IconButton>
                  </StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
            {/* {documentsCount > pageSize && (
              <TableFooter>
                <TableRow>
                  <TablePagination
                    sx={TablePaginationStyle}
                    count={documentsCount}
                    page={page}
                    rowsPerPage={pageSize}
                    rowsPerPageOptions={[pageSize]}
                    onPageChange={handleChangePage}
                    labelDisplayedRows={paginationLabel}
                  />
                </TableRow>
              </TableFooter>
            )} */}
          </>
        )}
      </Table>
      {!loading && documentsData.length === 0 && (
        <NoDataContainer>
          <Typography variant="body1" color="gray">
            No documents found
          </Typography>
        </NoDataContainer>
      )}
      {loading && (
        <Box sx={{ width: "100%" }}>
          <LinearProgress />
        </Box>
      )}
      {showVersionModal && selectedDocument && (
        <VersionHistoryModal
          showModal={showVersionModal}
          closeModal={closeVersionModal}
          documentName={selectedDocument.documentName}
          documentType={selectedDocument.type}
          documentCategory={selectedDocument.category}
          documentId={selectedDocument.id}
        />
      )}

      {showAddVersionModal && selectedDocument && (
        <AddVersionModal
          showModal={showAddVersionModal}
          closeModal={closeAddVersionModal}
          documentId={selectedDocument.id}
          documentName={selectedDocument.documentName}
          documentCategory={selectedDocument.category}
          documentType={selectedDocument.type}
          onVersionAdded={refreshDocumentsData}
        />
      )}

      {showEditModal && selectedDocument && (
        <EditDocumentModal
          showModal={showEditModal}
          closeModal={closeEditModal}
          documentId={selectedDocument.id}
          documentName={selectedDocument.documentName}
          documentCategory={selectedDocument.category}
          documentType={selectedDocument.type}
          onDocumentUpdated={refreshDocumentsData}
        />
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        TransitionProps={{
          exit: false,
        }}
      >
        {!isLocked && (
          <MenuItem onClick={() => handleEditDocument(selectedRow)}>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {!isLocked && selectedRow?.status === "active" && (
          <MenuItem onClick={() => handleAddNewVersion(selectedRow)}>
            <ListItemText>Add new version</ListItemText>
          </MenuItem>
        )}
        {!isLocked && (
          <MenuItem
            onClick={() => handleStatusChange(selectedRow)}
            disabled={!!menuLoader}
          >
            {menuLoader === "statusChange" && (
              <ListItemIcon>
                <CircularProgress size={18} />
              </ListItemIcon>
            )}
            <ListItemText>
              {selectedRow?.status === "active" ? "De-activate" : "Activate"}
            </ListItemText>
          </MenuItem>
        )}
        {isLocked && (
          <MenuItem disabled>
            <ListItemText>{PARTICIPANT_LOCKED_MESSAGE}</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default GlobalDocuments;
